import isEqual from "lodash/isEqual";
import BillingsTaxiTransferredOrderListingFilterType from "../types/billings-taxi-transferred-order-listing-filter-type";
import BillingsTaxiTransferredOrderListingItemBillingStatus from "../types/billings-taxi-transferred-order-listing-item-billing-status";
import billingsTaxiTransferredOrderListingHelper from "./billings-taxi-transferred-order-listing.helper";
import BillingsTaxiTransferredOrderListingFilterSelectOption, {
  BillingsTaxiTransferredOrderListingInternalOrderIdFilterSelectOption,
} from "../types/billings-taxi-transferred-order-listing-filter-select-option";
import billingsTranslationsHelper from "../../../../../../languages/billings-translations.helper";
import BillingsTaxiTransferredOrderListingFilter, {
  BillingsTaxiTransferredOrderListingOrderStartDateFilter,
} from "../types/billings-taxi-transferred-order-listing-filter";

const getSearchQueryOptions = (
  query: string
): BillingsTaxiTransferredOrderListingFilterSelectOption[] => {
  if (!query) {
    return [];
  }

  const searchFilterTranslations =
    billingsTranslationsHelper.getTaxiRouteTransferredOrderBillingsListingTranslations()
      .filters.search;

  const parseStringToNumber = (string: string): number | undefined => {
    const stringAsNumber = Number(string);

    if (Number.isNaN(stringAsNumber)) {
      return undefined;
    }

    return stringAsNumber;
  };

  const getSearchByInternalOrderIdLabel = (query: string) => {
    return searchFilterTranslations.searchByInternalOrderIdTemplateText.replace(
      `#{query}`,
      query
    );
  };

  const getSearchByProducerTaxiLabel = (query: string) => {
    return searchFilterTranslations.searchByProducerTaxiTemplateText.replace(
      `#{query}`,
      query
    );
  };

  const getSearchByConsumerTaxiLabel = (query: string) => {
    return searchFilterTranslations.searchByConsumerTaxiTemplateText.replace(
      `#{query}`,
      query
    );
  };

  const stringOptions = [
    {
      label: getSearchByProducerTaxiLabel(query),
      value: {
        type: BillingsTaxiTransferredOrderListingFilterType.PRODUCER_TAXI,
        value: query,
      },
    },
    {
      label: getSearchByConsumerTaxiLabel(query),
      value: {
        type: BillingsTaxiTransferredOrderListingFilterType.CONSUMER_TAXI,
        value: query,
      },
    },
  ];

  const options: BillingsTaxiTransferredOrderListingFilterSelectOption[] = [
    ...stringOptions,
  ] as BillingsTaxiTransferredOrderListingFilterSelectOption[];

  if (parseStringToNumber(query) !== undefined) {
    const routeIdFilterOption: BillingsTaxiTransferredOrderListingInternalOrderIdFilterSelectOption =
      {
        label: getSearchByInternalOrderIdLabel(query),
        value: {
          type: BillingsTaxiTransferredOrderListingFilterType.INTERNAL_ORDER_ID,
          value: Number(query),
        },
      };

    options.push(routeIdFilterOption);
  }

  return options;
};

const getSearchStatusOptions = (
  query: string
): BillingsTaxiTransferredOrderListingFilterSelectOption[] => {
  const searchFilterTranslations =
    billingsTranslationsHelper.getTaxiRouteTransferredOrderBillingsListingTranslations()
      .filters.search;

  const allSelectOptions: BillingsTaxiTransferredOrderListingFilterSelectOption[] =
    Object.values(BillingsTaxiTransferredOrderListingItemBillingStatus).map(
      (status) => {
        const label =
          searchFilterTranslations.searchByBillingStatusTemplateText.replace(
            "#{option}",
            billingsTaxiTransferredOrderListingHelper.getBillingStatusText(
              status
            )
          );

        return {
          label,
          value: {
            type: BillingsTaxiTransferredOrderListingFilterType.BILLING_STATUS,
            value: status,
          },
        };
      }
    );

  if (query) {
    return allSelectOptions.filter((option) =>
      option.label.toLowerCase().includes(query.toLowerCase())
    );
  }

  return allSelectOptions;
};

const getSelectOptionsByQuery = (
  query: string
): BillingsTaxiTransferredOrderListingFilterSelectOption[] => {
  const queryOptions = getSearchQueryOptions(query);
  const statusOptions = getSearchStatusOptions(query);

  return [...statusOptions, ...queryOptions];
};

const getSelectOptions = (
  query: string,
  selectedFilters: BillingsTaxiTransferredOrderListingFilter[]
): BillingsTaxiTransferredOrderListingFilterSelectOption[] => {
  const allFilterOptions = getSelectOptionsByQuery(query);

  return allFilterOptions.filter((option) => {
    return !selectedFilters.find((selectedFilter) =>
      isEqual(selectedFilter.type, option.value.type)
    );
  });
};

const getDefaultFilters = (
  filters: BillingsTaxiTransferredOrderListingFilter[]
): BillingsTaxiTransferredOrderListingFilter[] => {
  const currentDate = new Date();
  const timezoneOffset = currentDate.getTimezoneOffset();

  const defaultFilters = [
    {
      type: BillingsTaxiTransferredOrderListingFilterType.ORDER_START_DATE,
      value: {
        from: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
        to: new Date(
          new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() + 1,
            0
          ).getTime() -
            timezoneOffset * 60 * 1000
        ),
      },
    } as BillingsTaxiTransferredOrderListingOrderStartDateFilter,
  ];

  defaultFilters.forEach((defaultFilter) => {
    const existingFilter = filters.find(
      (filter) => filter.type === defaultFilter.type
    );

    if (existingFilter) {
      return;
    }

    filters.push(defaultFilter);
  });

  return filters;
};

const billingsTaxiTransferredOrderListingFilterHelper = {
  getDefaultFilters,
  getSelectOptions,
};

export default billingsTaxiTransferredOrderListingFilterHelper;
